import { useCallback } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';
import type { useWorkOrderMutations } from '~/app/hooks/maintenance-execution/useWorkOrderMutations';
import type {
  KanbanWorkOrderView,
  PendingAssignAction,
  PendingCompleteAction,
  PendingHoldAction,
  PendingMoveAction,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { WorkOrderStatus } from '@evimo/types/enums/maintenance-execution';
import {
  asOptionalString,
  canQualifyInterventionOnKanban,
  isKanbanColumnStatus,
  isTicketMoveTargetStatus,
  parseColumnDroppableId,
  parseTicketDraggableId,
  parseWorkOrderDraggableId,
  parseWorkOrderDroppableId,
  isWorkOrderEligibleToClose,
  resolveColumnTransition,
  type KanbanColumnStatus,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

type WorkOrderMutations = ReturnType<typeof useWorkOrderMutations>;

export type UseWorkOrderKanbanDragArgs = {
  canManage: boolean;
  workOrders: KanbanWorkOrderView[];
  mutations: WorkOrderMutations;
  message: MessageInstance;
  t: TFunction<'maintenance'>;
  setPendingAssign: (value: PendingAssignAction | null) => void;
  setPendingHold: (value: PendingHoldAction | null) => void;
  setPendingComplete: (value: PendingCompleteAction | null) => void;
  setPendingMove: (value: PendingMoveAction | null) => void;
};

/**
 * DnD handlers: WO column transitions and ticket drops onto another WO.
 */
export function useWorkOrderKanbanDrag(args: UseWorkOrderKanbanDragArgs) {
  const {
    canManage,
    workOrders,
    mutations,
    message,
    t,
    setPendingAssign,
    setPendingHold,
    setPendingComplete,
    setPendingMove,
  } = args;

  const handleInvalidTransition = useCallback(() => {
    message.warning(t('workOrders.kanban.messages.invalidTransition'));
  }, [message, t]);

  const handleWorkOrderColumnDrop = useCallback(
    (workOrderId: string, to: KanbanColumnStatus) => {
      if (!canManage) {
        message.warning(t('workOrders.kanban.messages.readOnly'));
        return;
      }
      const wo = workOrders.find((item) => item.id === workOrderId);
      if (!wo) return;

      const transition = resolveColumnTransition(wo.status, to);
      switch (transition.kind) {
        case 'assign':
          setPendingAssign({
            workOrderId: wo.id,
            workOrderCode: wo.code,
          });
          break;
        case 'start':
          mutations.start.mutate(wo.id, {
            onError: () =>
              message.error(t('workOrders.kanban.messages.startFailed')),
          });
          break;
        case 'hold':
          setPendingHold({
            workOrderId: wo.id,
            workOrderCode: wo.code,
          });
          break;
        case 'complete':
          setPendingComplete({
            workOrderId: wo.id,
            workOrderCode: wo.code,
            tickets: wo.tickets,
          });
          break;
        case 'closeWorkOrder':
          if (!isWorkOrderEligibleToClose(wo)) {
            message.warning(
              t('workOrders.kanban.messages.closeWorkOrderNotReady'),
            );
            return;
          }
          mutations.closeWorkOrder.mutate(wo.id, {
            onError: () =>
              message.error(
                t('workOrders.kanban.messages.closeWorkOrderFailed'),
              ),
          });
          break;
        default:
          handleInvalidTransition();
      }
    },
    [
      canManage,
      handleInvalidTransition,
      message,
      mutations.closeWorkOrder,
      mutations.start,
      setPendingAssign,
      setPendingComplete,
      setPendingHold,
      t,
      workOrders,
    ],
  );

  const handleTicketDropOnClosedColumn = useCallback(
    (ticketId: string, sourceWorkOrderId: string) => {
      if (!canManage) {
        message.warning(t('workOrders.kanban.messages.readOnly'));
        return;
      }
      const source = workOrders.find((wo) => wo.id === sourceWorkOrderId);
      const ticket = source?.tickets.find((item) => item.id === ticketId);
      if (!source || !ticket) {
        message.warning(t('workOrders.kanban.messages.invalidTicketDrop'));
        return;
      }
      if (!canQualifyInterventionOnKanban(source, ticket)) {
        message.warning(
          t('workOrders.kanban.messages.closeInterventionNotAllowed'),
        );
        return;
      }
      mutations.closeIntervention.mutate(
        { workOrderId: source.id, ticketId: ticket.id },
        {
          onError: () =>
            message.error(
              t('workOrders.kanban.messages.closeInterventionFailed'),
            ),
        },
      );
    },
    [canManage, message, mutations.closeIntervention, t, workOrders],
  );

  const handleTicketDropOnWorkOrder = useCallback(
    (
      ticketId: string,
      sourceWorkOrderId: string,
      targetWorkOrderId: string,
    ) => {
      if (!canManage) {
        message.warning(t('workOrders.kanban.messages.readOnly'));
        return;
      }
      if (sourceWorkOrderId === targetWorkOrderId) return;

      const source = workOrders.find((wo) => wo.id === sourceWorkOrderId);
      const target = workOrders.find((wo) => wo.id === targetWorkOrderId);
      if (!target || !isTicketMoveTargetStatus(target.status)) {
        message.warning(t('workOrders.kanban.messages.invalidTicketDrop'));
        return;
      }

      const ticket = source?.tickets.find((item) => item.id === ticketId);
      if (!ticket || !source) {
        message.warning(t('workOrders.kanban.messages.invalidTicketDrop'));
        return;
      }

      setPendingMove({
        sourceWorkOrderId: source.id,
        sourceWorkOrderCode: source.code,
        sourceSiteId: source.siteId,
        sourceSiteName: source.siteName,
        ticketId: ticket.id,
        ticketCode: ticket.code,
        targetWorkOrderId: target.id,
        targetWorkOrderCode: target.code,
        targetSiteId: target.siteId,
        targetSiteName: target.siteName,
      });
    },
    [canManage, message, setPendingMove, t, workOrders],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      const ticketId = parseTicketDraggableId(activeId);
      if (ticketId) {
        const sourceWorkOrderId = asOptionalString(
          active.data.current?.workOrderId,
        );
        if (!sourceWorkOrderId) return;

        const columnStatus = parseColumnDroppableId(overId);
        if (columnStatus === WorkOrderStatus.CLOSED) {
          handleTicketDropOnClosedColumn(ticketId, sourceWorkOrderId);
          return;
        }

        const targetWorkOrderId =
          parseWorkOrderDroppableId(overId) ??
          parseWorkOrderDraggableId(overId);
        if (!targetWorkOrderId) {
          message.warning(t('workOrders.kanban.messages.invalidTicketDrop'));
          return;
        }
        handleTicketDropOnWorkOrder(
          ticketId,
          sourceWorkOrderId,
          targetWorkOrderId,
        );
        return;
      }

      const workOrderId = parseWorkOrderDraggableId(activeId);
      if (!workOrderId) return;

      const columnStatus = parseColumnDroppableId(overId);
      if (columnStatus) {
        handleWorkOrderColumnDrop(workOrderId, columnStatus);
        return;
      }

      // Dropped on another WO card / droppable — treat as that WO's column.
      const overWorkOrderId =
        parseWorkOrderDroppableId(overId) ?? parseWorkOrderDraggableId(overId);
      if (!overWorkOrderId || overWorkOrderId === workOrderId) return;

      const overStatus = asOptionalString(over.data.current?.status);
      if (overStatus && isKanbanColumnStatus(overStatus)) {
        handleWorkOrderColumnDrop(workOrderId, overStatus);
        return;
      }

      const overWo = workOrders.find((item) => item.id === overWorkOrderId);
      if (overWo && isKanbanColumnStatus(overWo.status)) {
        handleWorkOrderColumnDrop(workOrderId, overWo.status);
      }
    },
    [
      handleTicketDropOnClosedColumn,
      handleTicketDropOnWorkOrder,
      handleWorkOrderColumnDrop,
      message,
      t,
      workOrders,
    ],
  );

  return { onDragEnd };
}
